import {_} from 'lodash';
import * as ko from "knockout";
import PromiseRequest from "../../../req/PromiseRequest";
import {DropdownMenu} from "../../dropdown/menu";
import {IMessageData, IRuleset} from "../../../index";
import {KOSubscriptionTracker} from "../../../util/KOSubscriptionTracker";
import {createErrorMessage} from "../../../util/misc";
import {global} from "../../../appContext";
import {nsRulesetVariableCrud} from "./nsRulesetVariableCrud";
import TOperation = nsRulesetVariableCrud.TOperation;
import crudHtml from './crud.html?raw';

export const template = crudHtml

interface IOperation {
    name: TOperation
    callback: (ruleset?: IRuleset | null) => void
}

export interface IRulesetVariableCrud {
    observable: KnockoutObservable<IRuleset['id']>
    operations: (IOperation | TOperation)[]
}

export class viewModel {
    private readonly ruleset: KnockoutObservable<IRuleset | null> = ko.observable(null);
    protected readonly id: KnockoutObservable<IRuleset["id"]>;

    private readonly kosub: KOSubscriptionTracker = new KOSubscriptionTracker();
    private readonly messageData: KnockoutObservable<IMessageData | null> = ko.observable(null);

    private readonly req_getruleset: PromiseRequest = global.getPromiseRequest('getruleset')
    private readonly dropDownMenu: DropdownMenu

    constructor(params: IRulesetVariableCrud) {
        this.id = params.observable

        this.dropDownMenu = new DropdownMenu({
            component: {
                name: 'component/variable/ruleset',
                params: {id: this.id},
            }
        })

        this.dropDownMenu.button_tt = 'manage subject|s:((ruleset|ac))'

        params.operations.forEach((operation: IOperation | TOperation) => {
            const items = this.dropDownMenu.items;
            let callback: nsRulesetVariableCrud.TCallback
            let name: nsRulesetVariableCrud.TOperation
            if (typeof operation === 'string') {
                name = operation
                callback = o => {
                    this.id(0)
                    requestAnimationFrame(() => this.id(o?.id ?? 0))
                }
            } else {
                name = operation.name
                callback = operation.callback
            }
            switch (name) {
                case "preview": {
                    items.push({
                        onClick: () => {
                            const ruleset = this.ruleset();
                            if (ruleset) {
                                nsRulesetVariableCrud.showDialog_preview(ruleset);
                            }
                        },
                        description: 'preview subject|s:((ruleset|ac))',
                        enabled: () => !!this.ruleset(),
                        icon: 'fa-eye',
                        label: 'preview',
                        validator: () => !!this.ruleset(),
                    })
                    break;
                }
                case "update": {
                    items.push({
                        onClick: () => {
                            const ruleset = this.ruleset();
                            if (ruleset) {
                                nsRulesetVariableCrud.showDialog_update(ruleset, callback);
                            }
                        },
                        description: 'edit subject|s:((ruleset|ac))',
                        enabled: () => !!this.ruleset(),
                        icon: 'fa-pencil',
                        label: 'edit',
                        validator: () => !!this.ruleset(),
                    })
                    break;
                }
                case "read": {
                    items.push({
                        onClick: () => {
                            const ruleset = this.ruleset();
                            if (ruleset) {
                                nsRulesetVariableCrud.showDialog_read(ruleset);
                            }
                        },
                        enabled: () => !!this.ruleset(),
                        icon: 'fa-ruleset',
                        label: 'see details',
                        validator: () => !!this.ruleset(),
                    })
                    break;
                }
                case "pick": {
                    items.push({
                        onClick: () => nsRulesetVariableCrud.showDialog_pick(callback),
                        description: 'choose subject|s:((ruleset|ac))',
                        icon: 'fa-hand-point-up',
                        label: 'choose',
                    })
                    break;
                }
                case "create": {
                    items.push({
                        onClick: () => nsRulesetVariableCrud.showDialog_create(callback),
                        description: 'create subject|s:((ruleset|ac))',
                        icon: 'fa-plus',
                        label: 'create',
                    })
                    break;
                }
                case "remove": {
                    items.push({
                        onClick: () => {
                            callback(null)
                            this.id(0)
                        },
                        description: 'remove s|s:((ruleset|ac))',
                        icon: 'fa-times',
                        label: 'remove',
                        validator: () => !!this.id(),
                    })
                    break;
                }
            }
        })

        this.kosub.subscribe(this.id, this.request.bind(this))

        if (this.id()) {
            this.request();
        }
    }

    private request(): Promise<any> {
        this.req_getruleset.abort()

        if (!this.id()) {
            this.ruleset(null)
            return Promise.resolve()
        }

        return this.req_getruleset
            .request({id: this.id()})
            .then((data: [IRuleset]) => {
                this.ruleset(data[0])
                this.messageData(null)
            })
            .catch((reason: string) => this.messageData(createErrorMessage(String(reason))))
    }

    dispose() {
        this.kosub.unsuscribeAll()
        this.req_getruleset.abort()
    }
}
